import React, { useEffect, useState } from 'react'
import { useTranslation } from 'i18n'
import ServingLogo from 'images/inline/servings.svg'
import TimeLogo from 'images/inline/time.svg'
import expandLogo from 'images/expand.svg'
import closeLogo from 'images/close.svg'
import CopyToClipboardButton from './CopyToClipboardButton'
import analytics from 'global/AnalyticsData'

import './ResultPanel.scss'
import { useMemo } from 'react'
import OptionalDropdownButton from '../../components/OptionalDropdownButton'

const validateResponseFormat = response => {
  try {
    const requiredKeys = [
      'name',
      'cooking_time',
      'serving_size',
      'description',
      'ingredients',
      'instructions',
    ]

    // Log the keys in the response to check if it has all required ones
    const responseKeys = Object.keys(response)
    console.log('Response keys:', responseKeys)

    // Check if response is an object and has all required keys
    const hasAllKeys = requiredKeys.every(key => key in response)
    console.log('Has all required keys:', hasAllKeys)

    if (typeof response === 'object' && hasAllKeys) {
      return true
    } else {
      console.warn(
        'Missing keys in response:',
        requiredKeys.filter(key => !(key in response)),
      )
    }
  } catch (error) {
    console.warn('Response validation failed:', error)
  }

  return false
}

export const ResultPanel = ({
  recipe = {},
  number,
  state,
  step,
  keyList,
  dropdownButtons,
}) => {
  const [cardExpanded, setCardExpanded] = useState(number === 0)
  const [isGenerating, setIsGenerating] = useState(true)
  const [isValidRecipe, setIsValidRecipe] = useState(false)

  const { t } = useTranslation('grocery')

  useEffect(() => {
    if (Object.keys(recipe).length > 0) {
      const valid = validateResponseFormat(recipe)
      setIsValidRecipe(valid)
      setIsGenerating(false)
    }
  }, [recipe])

  const handleResultLinkClick = async (id, text, url) => {
    analytics.stepEvent('step_button_click', step, {
      result_id: id,
      result: text + ' | ' + url,
    })
  }

  const {
    name,
    cooking_time,
    serving_size,
    description,
    ingredients,
    instructions,
  } = recipe

  const formattedServingSize =
    serving_size &&
    (!isNaN(serving_size)
      ? serving_size +
        ' ' +
        (parseInt(serving_size) === 1
          ? t('recipe.serving')
          : t('recipe.servings'))
      : serving_size.includes('servings') || serving_size.includes('porciones')
      ? serving_size
      : serving_size + ' ' + t('recipe.servings'))

  const referenceName = [
    { rName: 'Recipe Name', content: name },
    { rName: 'Description', content: description },
    { rName: 'Cooking Time', content: cooking_time },
    { rName: 'Serving Size', content: formattedServingSize },
    { rName: 'Ingredients', content: ingredients },
    { rName: 'Instructions', content: instructions },
  ]

  const firstKeyItemShown = useMemo(() => {
    try {
      if (!ingredients || !keyList) {
        return null
      }

      let firstKeyItem = null
      ingredients.forEach(ingredient => {
        if (firstKeyItem) return
        const foundKeyItem = keyList.find(keyItem => {
          return ingredient
            .toLowerCase()
            .includes(keyItem?.keyWord.toLowerCase())
        })
        if (foundKeyItem) {
          firstKeyItem = foundKeyItem
        }
      })

      return firstKeyItem || null
    } catch (error) {
      console.error('Error finding first key item:', error)
      return null
    }
  }, [ingredients, keyList])

  const handleExpandOnClick = () => {
    setCardExpanded(true)
    analytics.stepEvent('step_interaction_genai_result_open', step)
  }

  const handleCloseOnClick = () => {
    setCardExpanded(false)
  }

  const handleCopyOnClick = () => {
    const text = referenceName.map(item => {
      if (item.rName === 'Ingredients' || item.rName === 'Instructions') {
        return (
          item.rName +
          ': ' +
          '\n' +
          item.content.reduce(
            (prev, cur, i) => prev + '  ' + (i + 1) + '. ' + cur + '\n',
            '',
          )
        )
      }
      return item.rName + ': ' + item.content + '\n'
    })
    navigator.clipboard.writeText(text.reduce((prev, cur) => prev + cur))
    analytics.stepEvent('step_interaction_genai_result_copy', step)
  }

  const FormatedIngredient = ({ item }) => {
    if (typeof item === 'string') {
      return item
    } else if (typeof item === 'object') {
      if (Object.keys(item).includes('name')) {
        return item.name
      } else if (Object.keys(item).includes('ingredient')) {
        return item.ingredient
      }
    }
    return item
  }

  const CursorSvg = () => {
    return (
      <svg
        viewBox="8 4 8 16"
        xmlns="http://www.w3.org/2000/svg"
        className="cursor"
      >
        <rect x="12" y="0" width="4" height="30" fill="#000000" />
      </svg>
    )
  }

  const smallCard = (
    <>
      <div className="expand-logo" onClick={handleExpandOnClick}>
        <img src={expandLogo} alt="expand-card" />
      </div>
      <div className="small-content" onClick={handleExpandOnClick}>
        <h2 className="content-title">
          {recipe.name || t('recipe.cooking')}
          {isGenerating && <CursorSvg />}
        </h2>
        <div className="content-body">
          <div className="text logo">
            <ServingLogo />
            <span className="bodytext">{formattedServingSize}</span>
          </div>
          <div className="text logo" style={{ padding: '0px 2px' }}>
            <TimeLogo />
            <span className="bodytext">{cooking_time}</span>
          </div>
        </div>
        <div className="see-recipe">{t('recipe.seeRecipe')}</div>
      </div>
    </>
  )

  const expandedCard = (
    <>
      <div className="close-logo" onClick={handleCloseOnClick}>
        <img src={closeLogo} alt="close-expanded-card" />
      </div>
      <div className="expand-content">
        {isGenerating ? (
          <div className="typing-card">
            <span dangerouslySetInnerHTML={{ __html: state.text }} />
            <CursorSvg />
          </div>
        ) : isValidRecipe ? (
          <>
            <h1 className="content-title">{recipe.name}</h1>
            <div className="content-body">
              <div className="left">
                <div className="icons">
                  <div className="text logo">
                    <ServingLogo />
                    <span className="bodytext">{recipe.serving_size}</span>
                  </div>
                  <div className="text logo" style={{ padding: '0px 2px' }}>
                    <TimeLogo />
                    <span className="bodytext">{recipe.cooking_time}</span>
                  </div>
                </div>
                <div className="product-image">
                  {firstKeyItemShown?.imageId ? (
                    <img
                      className="img"
                      alt={firstKeyItemShown?.keyIngredient || ''}
                      src={`${process.env.SITE_ASSET_URL}/${firstKeyItemShown.imageId}`}
                    />
                  ) : (
                    step?.globalCTAImage && (
                      <img
                        className="img"
                        alt={step.globalCTAImage.name || ''}
                        src={`${process.env.SITE_ASSET_URL}/${step.globalCTAImage.id}`}
                      />
                    )
                  )}
                  {firstKeyItemShown?.keyIngredient ? (
                    <div className="key-ingredient">
                      <b>{t('recipe.keyIngredientLabel')}</b>
                      {firstKeyItemShown.keyIngredient}
                    </div>
                  ) : (
                    step?.globalKeyIngredient && (
                      <div className="key-ingredient">
                        <b>{t('recipe.keyIngredientLabel')}</b>
                        {step.globalKeyIngredient}
                      </div>
                    )
                  )}
                </div>

                {dropdownButtons[firstKeyItemShown?.keyWord] ? (
                  <div style={{ marginTop: '20px' }}>
                    {dropdownButtons[firstKeyItemShown?.keyWord].map(
                      (dropdownButton, i) => {
                        return (
                          <OptionalDropdownButton
                            key={i}
                            id={dropdownButtons.id}
                            label={dropdownButton.buttonCopy}
                            links={dropdownButton.links}
                            onLinkClick={handleResultLinkClick}
                          />
                        )
                      },
                    )}
                  </div>
                ) : (
                  step?.resultDropdownButtons && (
                    <div style={{ marginTop: '20px' }}>
                      {step.resultDropdownButtons.map((dropdownButton, i) => {
                        return (
                          <OptionalDropdownButton
                            key={i}
                            id={dropdownButtons.id}
                            label={dropdownButton.buttonCopy}
                            links={dropdownButton.links}
                            onLinkClick={handleResultLinkClick}
                          />
                        )
                      })}
                    </div>
                  )
                )}
                <div className="ingredients">
                  <h2 className="title">{t('recipe.ingredientsHeading')}</h2>
                  {recipe.ingredients?.map((item, i) => (
                    <div className="body" key={'ingredients' + i}>
                      <span style={{ paddingRight: '5px' }}>{'\u2022'}</span>
                      {typeof item === 'object'
                        ? item.name || item.ingredient
                        : item}
                    </div>
                  ))}
                </div>
              </div>
              <div className="right">
                <div className="description">{recipe.description}</div>
                <div className="instructions">
                  <h2 className="title">{t('recipe.instructionsHeading')}</h2>
                  {recipe.instructions?.map((item, i) => (
                    <div className="body" key={'instructions' + i}>
                      <span style={{ paddingRight: '5px' }}>
                        {i + 1 + '. '}
                      </span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <CopyToClipboardButton
              id="copy"
              label={t('copyToClipboard')}
              onClick={() => {
                const text = recipe.ingredients
                  .map((ing, i) => `${i + 1}. ${ing}`)
                  .join('\n')
                navigator.clipboard.writeText(text)
                analytics.stepEvent('step_interaction_genai_result_copy', step)
              }}
            />

            <div className="close-recipe" onClick={handleCloseOnClick}>
              {t('recipe.closeRecipe')}
            </div>
          </>
        ) : (
          <div className="error-message">
            <p>{t('recipe.notFoundError')}</p>
          </div>
        )}
      </div>
    </>
  )

  return (
    <div className={'ResultPanel-recipe ' + (cardExpanded ? 'expand' : '')}>
      {cardExpanded ? (
        expandedCard
      ) : isGenerating ? (
        <>
          <div className="expand-logo" onClick={handleExpandOnClick}>
            <img src={expandLogo} alt="expand-card" />
          </div>
          <div className="small-content" onClick={handleExpandOnClick}>
            <h2 className="content-title">
              {recipe.name || t('recipe.cooking')}
              {isGenerating && <CursorSvg />}
            </h2>
          </div>
        </>
      ) : isValidRecipe ? (
        smallCard
      ) : null}
      {!isGenerating && !isValidRecipe && !cardExpanded && (
        <div className="error-message">
          <p>{t('recipe.notFoundError')}</p>
        </div>
      )}
    </div>
  )
}

export default ResultPanel
